/**
 * useHelpArticle Hook
 *
 * Purpose: Fetch a single help article by slug
 * Created: March 24, 2026
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HelpService } from '../services/HelpService';
import { HelpArticle } from '@xanectech/types';
import { toast } from '@xanectech/ui/lib';
import { getErrorMessage, logger } from '@xanectech/lib';

export const useHelpArticle = (
  slug?: string | null,
  articleId?: string | null,
) => {
  return useQuery({
    queryKey: ['help-article', slug, articleId],
    queryFn: () => {
      if (slug) return HelpService.getArticleBySlug(slug);
      if (articleId) return HelpService.getArticleById(articleId);
      return Promise.resolve(null);
    },
    enabled: !!slug || !!articleId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useHelpArticles = () => {
  return useQuery({
    queryKey: ['help-articles'],
    queryFn: () => HelpService.getAllArticles(),
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateHelpArticle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (newArticle: HelpArticle) =>
      HelpService.createArticle(newArticle),
    onSuccess: (createdArticle) => {
      if (!createdArticle) return;
      // Invalidate related queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['help-articles'] });
      queryClient.invalidateQueries({
        queryKey: ['help-article', createdArticle.slug],
      });
      toast({ title: 'Help article created successfully', variant: 'success' });
    },
    onError: (error) => {
      toast({
        title: 'Error creating help article',
        description:
          error instanceof Error ? error.message : 'Unknown error occurred',
        variant: 'destructive',
      });
      logger.error('createHelpArticle error:', {
        error: getErrorMessage(error),
      });
    },
  });
};

export const useUpdateHelpArticle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (updatedArticle: Partial<HelpArticle>) =>
      HelpService.updateArticle(updatedArticle.id!, updatedArticle),
    onSuccess: (updated) => {
      if (!updated) return;
      queryClient.invalidateQueries({ queryKey: ['help-articles'] });
      queryClient.invalidateQueries({
        queryKey: ['help-article', updated.slug],
      });
      toast({ title: 'Help article updated successfully', variant: 'success' });
    },
    onError: (error) => {
      toast({
        title: 'Error updating help article',
        description:
          error instanceof Error ? error.message : 'Unknown error occurred',
        variant: 'destructive',
      });
      logger.error('updateHelpArticle error:', {
        error: getErrorMessage(error),
      });
    },
  });
};

export const useDeleteHelpArticle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (articleId: string) => HelpService.deleteArticle(articleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['help-articles'] });
      toast({ title: 'Help article deleted successfully', variant: 'success' });
    },
    onError: (error) => {
      toast({
        title: 'Error deleting help article',
        description:
          error instanceof Error ? error.message : 'Unknown error occurred',
        variant: 'destructive',
      });
      logger.error('deleteHelpArticle error:', {
        error: getErrorMessage(error),
      });
    },
  });
};
